import React from 'react';
import { FONT_BODY } from './theme';
import { ROLES, BOARDS, SIZES, HELP } from './data';
import { ModalShell, FieldLabel, FieldError, TextField, SelectField, CtaButton, Logo, IconCloseButton } from './ui';
import { ArrowIcon, SuccessCheckIcon } from './icons';

export interface EnquiryForm {
  name: string;
  role: string;
  school: string;
  wa: string;
  email: string;
  city: string;
  board: string;
  size: string;
  help: string[];
}

export const blankEnquiryForm = (): EnquiryForm => ({
  name: '',
  role: '',
  school: '',
  wa: '',
  email: '',
  city: '',
  board: '',
  size: '',
  help: [],
});

export type EnquiryErrors = Record<'name' | 'school' | 'city' | 'board' | 'size' | 'wa' | 'email', boolean>;

export function enquiryErrors(f: EnquiryForm): EnquiryErrors {
  return {
    name: f.name.trim().length < 2,
    school: f.school.trim().length < 2,
    city: f.city.trim().length < 2,
    board: !f.board,
    size: !f.size,
    wa: !/^[6-9][0-9]{9}$/.test(f.wa),
    email: !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim()),
  };
}

const Required: React.FC = () => <span style={{ color: '#DC2626' }}>*</span>;
const Optional: React.FC = () => (
  <span style={{ font: `500 11.5px ${FONT_BODY}`, color: '#64748B' }}>(optional)</span>
);

const LINK_BUTTON: React.CSSProperties = {
  border: 0,
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  font: `600 12px ${FONT_BODY}`,
  color: '#2563EB',
};

const EnquiryModal: React.FC<{
  f: EnquiryForm;
  tried: boolean;
  sent: boolean;
  onClose: () => void;
  onField: (k: keyof EnquiryForm, v: string) => void;
  onToggleHelp: (h: string) => void;
  onSend: () => void;
  onTerms: () => void;
  onPrivacy: () => void;
}> = ({ f, tried, sent, onClose, onField, onToggleHelp, onSend, onTerms, onPrivacy }) => {
  const errs = enquiryErrors(f);
  const e: Partial<EnquiryErrors> = tried ? errs : {};
  const bd = (bad?: boolean) => (bad ? '#F87171' : '#CFDFF6');
  const firstName = f.name.trim().split(/\s+/)[0] || 'there';
  const waFmt = `${f.wa.slice(0, 5)} ${f.wa.slice(5)}`;

  return (
    <ModalShell
      zIndex={105}
      overlayPadding="clamp(10px,3vw,32px)"
      panelMaxWidth={600}
      panelStyle={{ maxHeight: '100%', display: 'flex', flexDirection: 'column' }}
      onClose={onClose}
      dialogLabel="Send an enquiry"
    >
      <div
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '15px 20px',
          borderBottom: '1px solid #F1F6FD',
        }}
      >
        <Logo width={98} height={18} />
        <span style={{ font: `700 13.5px ${FONT_BODY}`, color: '#0F172A' }}>Talk to Soomuch.AI</span>
        <IconCloseButton onClick={onClose} label="Close" />
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          padding: 'clamp(18px,3vw,26px)',
        }}
      >
        {!sent && (
          <>
            <h3
              style={{
                font: `800 clamp(19px,2.4vw,22px)/1.2 ${FONT_BODY}`,
                letterSpacing: '-.028em',
                color: '#0F172A',
                margin: 0,
              }}
            >
              Send an enquiry
            </h3>
            <p style={{ font: `400 13px/1.5 ${FONT_BODY}`, color: '#64748B', margin: '6px 0 18px' }}>
              Fields marked <Required /> are required.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,210px),1fr))',
                gap: '13px 12px',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <FieldLabel marginBottom={6}>
                  Full name <Required />
                </FieldLabel>
                <TextField
                  value={f.name}
                  onChange={(ev) => onField('name', ev.target.value)}
                  autoComplete="name"
                  placeholder="Your name"
                  borderColor={bd(e.name)}
                />
                {e.name && <FieldError>Enter your full name.</FieldError>}
              </div>
              <div style={{ minWidth: 0 }}>
                <FieldLabel marginBottom={6}>
                  Designation <Optional />
                </FieldLabel>
                <SelectField
                  value={f.role}
                  onChange={(ev) => onField('role', ev.target.value)}
                  placeholderLabel="Select role"
                  options={ROLES}
                />
              </div>
              <div style={{ minWidth: 0, gridColumn: '1/-1' }}>
                <FieldLabel marginBottom={6}>
                  School / institution name <Required />
                </FieldLabel>
                <TextField
                  value={f.school}
                  onChange={(ev) => onField('school', ev.target.value)}
                  autoComplete="organization"
                  placeholder="Your school's name"
                  borderColor={bd(e.school)}
                />
                {e.school && <FieldError>Enter your school name.</FieldError>}
              </div>
              <div
                style={{
                  minWidth: 0,
                  gridColumn: '1/-1',
                  background: '#F3F8FF',
                  border: '1px solid #D6E6FB',
                  borderRadius: 13,
                  padding: '12px 13px',
                }}
              >
                <FieldLabel marginBottom={6}>
                  WhatsApp number <Required />
                </FieldLabel>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span
                    style={{
                      flex: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 13px',
                      background: '#fff',
                      border: '1px solid #CFDFF6',
                      borderRadius: 11,
                      font: `600 15px ${FONT_BODY}`,
                      color: '#475569',
                    }}
                  >
                    +91
                  </span>
                  <TextField
                    value={f.wa}
                    onChange={(ev) => onField('wa', ev.target.value)}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    maxLength={10}
                    placeholder="10-digit mobile number"
                    borderColor={bd(e.wa)}
                    background="#fff"
                    fontWeight={600}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                </div>
                <p style={{ font: `400 12px/1.4 ${FONT_BODY}`, color: '#475569', margin: '7px 0 0' }}>
                  We&apos;ll use this to connect with you about your school.
                </p>
                {e.wa && <FieldError>Enter a valid 10-digit mobile number.</FieldError>}
              </div>
              <div style={{ minWidth: 0 }}>
                <FieldLabel marginBottom={6}>
                  Email address <Required />
                </FieldLabel>
                <TextField
                  value={f.email}
                  onChange={(ev) => onField('email', ev.target.value)}
                  type="email"
                  autoComplete="email"
                  placeholder="you@school.edu.in"
                  borderColor={bd(e.email)}
                />
                {e.email && <FieldError>Enter a valid email.</FieldError>}
              </div>
              <div style={{ minWidth: 0 }}>
                <FieldLabel marginBottom={6}>
                  City <Required />
                </FieldLabel>
                <TextField
                  value={f.city}
                  onChange={(ev) => onField('city', ev.target.value)}
                  autoComplete="address-level2"
                  placeholder="e.g. Hyderabad"
                  borderColor={bd(e.city)}
                />
                {e.city && <FieldError>Enter your city.</FieldError>}
              </div>
              <div style={{ minWidth: 0 }}>
                <FieldLabel marginBottom={6}>
                  School type / board <Required />
                </FieldLabel>
                <SelectField
                  value={f.board}
                  onChange={(ev) => onField('board', ev.target.value)}
                  placeholderLabel="Select board"
                  options={BOARDS}
                  borderColor={bd(e.board)}
                />
                {e.board && <FieldError>Select a board.</FieldError>}
              </div>
              <div style={{ minWidth: 0 }}>
                <FieldLabel marginBottom={6}>
                  Number of students <Required />
                </FieldLabel>
                <SelectField
                  value={f.size}
                  onChange={(ev) => onField('size', ev.target.value)}
                  placeholderLabel="Select range"
                  options={SIZES}
                  borderColor={bd(e.size)}
                />
                {e.size && <FieldError>Select a range.</FieldError>}
              </div>
              <div style={{ minWidth: 0, gridColumn: '1/-1' }}>
                <FieldLabel marginBottom={6}>
                  Interested in <Optional />
                </FieldLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {HELP.map((h) => {
                    const on = f.help.includes(h);
                    return (
                      <button
                        key={h}
                        type="button"
                        onClick={() => onToggleHelp(h)}
                        aria-pressed={on}
                        className="sm1p-chip"
                        style={{
                          border: `1px solid ${on ? '#2563EB' : '#E3EBF7'}`,
                          background: on ? '#2563EB' : '#fff',
                          color: on ? '#fff' : '#334155',
                          borderRadius: 999,
                          padding: '7px 12px',
                          font: `600 12.5px ${FONT_BODY}`,
                          cursor: 'pointer',
                          transition: 'all .15s ease',
                        }}
                      >
                        {h}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <CtaButton
              variant="block"
              onClick={onSend}
              style={{
                width: '100%',
                margin: '20px 0 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                borderRadius: 12,
                padding: 15,
                font: `700 15.5px ${FONT_BODY}`,
                boxShadow: '0 12px 26px rgba(37,99,235,.26)',
              }}
            >
              Send Enquiry
              <ArrowIcon size={16} />
            </CtaButton>
            <p
              style={{
                font: `400 12px/1.55 ${FONT_BODY}`,
                color: '#64748B',
                margin: '11px 0 0',
                textAlign: 'center',
              }}
            >
              By sending, you agree to our{' '}
              <button type="button" onClick={onTerms} style={LINK_BUTTON}>
                Terms &amp; Conditions
              </button>{' '}
              and{' '}
              <button type="button" onClick={onPrivacy} style={LINK_BUTTON}>
                Privacy Policy
              </button>
              . Prototype: nothing is sent.
            </p>
          </>
        )}

        {sent && (
          <div style={{ textAlign: 'center', padding: 'clamp(14px,4vw,34px) 6px', animation: 'sm1p-slidein .45s ease both' }}>
            <div
              style={{
                width: 66,
                height: 66,
                margin: '0 auto 20px',
                borderRadius: '50%',
                background: '#E0EDFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 0 10px #F3F8FF',
                animation: 'sm1p-pop .55s cubic-bezier(.2,.8,.2,1) .1s both',
              }}
            >
              <SuccessCheckIcon />
            </div>
            <h3
              style={{
                font: `800 clamp(21px,2.6vw,25px)/1.25 ${FONT_BODY}`,
                letterSpacing: '-.03em',
                color: '#0F172A',
                margin: 0,
                textWrap: 'balance',
              }}
            >
              Thank you for your enquiry, {firstName}.
            </h3>
            <p
              style={{
                font: `400 15px/1.6 ${FONT_BODY}`,
                color: '#475569',
                margin: '12px auto 0',
                maxWidth: '40ch',
                textWrap: 'pretty',
              }}
            >
              We&apos;re glad you reached out. Our team will connect with you shortly on WhatsApp or email.
            </p>
            <p style={{ font: `500 13px/1.6 ${FONT_BODY}`, color: '#64748B', margin: '12px 0 0', overflowWrap: 'anywhere' }}>
              +91 {waFmt} · {f.email}
            </p>
            <CtaButton
              variant="block"
              onClick={onClose}
              style={{
                width: '100%',
                maxWidth: 280,
                margin: '24px auto 0',
                display: 'block',
                borderRadius: 12,
                padding: 14,
                font: `700 15px ${FONT_BODY}`,
              }}
            >
              Done
            </CtaButton>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default EnquiryModal;
